import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircleHeart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openModal = () => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setMessage("");
    setOpen(true);
  };

  // Allow other components (e.g., the end-of-report CTA) to open the modal
  useEffect(() => {
    const handler = () => openModal();
    window.addEventListener("cgi:open-feedback", handler);
    return () => window.removeEventListener("cgi:open-feedback", handler);
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please share a little bit of feedback before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        name: name.trim() || null,
        email: email.trim() || null,
        message: message.trim(),
        page: location.pathname,
      });
      toast.success("Thank you! Your feedback was sent to the Common Ground Institute team.");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Couldn't send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger — visible on every page */}
      <button
        type="button"
        onClick={openModal}
        data-testid="feedback-button-floating"
        aria-label="Send feedback"
        className="no-print fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all duration-200 hover:-translate-y-0.5 px-4 py-3 text-sm font-semibold"
      >
        <MessageCircleHeart className="h-5 w-5" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-cream border-amber-200" data-testid="feedback-dialog">
          <DialogHeader>
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-primary">
              This app is under active development
            </p>
            <DialogTitle className="font-heading text-2xl text-slate-900">
              Please… PLEASE! We want your feedback.
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Help us improve. Tell us what&apos;s working, what&apos;s confusing, and what
              you&apos;d change. Every note goes straight to the CGI team.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700" htmlFor="fb-name">
                  Your name <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <Input
                  id="fb-name"
                  data-testid="feedback-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="mt-1"
                  maxLength={120}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700" htmlFor="fb-email">
                  Email <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <Input
                  id="fb-email"
                  data-testid="feedback-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700" htmlFor="fb-message">
                Your feedback
              </label>
              <Textarea
                id="fb-message"
                data-testid="feedback-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What could we do better? Anything confusing, missing, or delightful?"
                className="mt-1 min-h-[140px]"
                maxLength={4000}
                required
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {message.length}/4000 characters
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="feedback-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !message.trim()}
                data-testid="feedback-submit-button"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
